import { SetStateAction, Dispatch } from 'react';
import { Mode } from 'react-hook-form';
import useAddMutation from 'src/compass/shared/hooks/reactQuery/useAddMutation';
import useUpdateMutation from 'src/compass/shared/hooks/reactQuery/useUpdateMutation';
import useUploadMutation from 'src/compass/shared/hooks/reactQuery/useUploadMutation';
import CasReactHookForm, { FormMethodType } from './CasReactHookForm';

/*
  TODO to be generic
    - Does it make sense to transform response to match consistent form object structure?
    - e.g.
      - response = {entity, asOfs:[]}
      - form = {entity, asOf:{}}
        + need asOfs list
*/

/*
  onBeforeSave:
    Generally a form should be designed to directly map to the data that will be
    posted to the API.  However, sometimes it may be required to re-shape the submitted
    data for the API.  In this case, onBeforeSave would be specified.  It may return a
    promise, for re-shaping that has to wait on something (hashing a picture, say).
*/
export interface ICasEditFormProps {
  defaultValues: any;
  entity: string;
  schema: any;
  children: any;
  onBeforeSave?: any;
  onSave?: any;
  onSaved?: any;
  fileUpload?: boolean;
  includesAttachment?: boolean;
  setFormMethods?: Dispatch<SetStateAction<FormMethodType>>;
  mode?: Mode;
}

export default function CasEditForm({
  defaultValues,
  entity,
  schema,
  children,
  onBeforeSave = null,
  onSave = null,
  onSaved = null,
  fileUpload = false,
  includesAttachment = false,
  setFormMethods,
  mode,
}: ICasEditFormProps) {
  const addMutation = useAddMutation({ url: entity, includesAttachment });
  const editMutation = useUpdateMutation({ url: entity, includesAttachment });
  const uploadMutation = useUploadMutation({ url: entity });

  const id = defaultValues?.id;
  const editMode = !!id;

  // The second arg is the API response body (e.g. the created record's Id) so callers
  // that need it can read it; existing single-arg handlers are unaffected.
  const handleOnSuccess = (payload: any, response?: any) => {
    if (onSaved) onSaved(payload, response);
  };

  const onSubmit = async (data: any) => {
    const payload = onBeforeSave ? await onBeforeSave(data) : data;

    if (onSave) {
      onSave(payload);
    } else if (fileUpload) {
      uploadMutation.mutate(payload, { onSuccess: (response) => handleOnSuccess(payload, response) });
    } else if (editMode) {
      editMutation.mutate(payload, { onSuccess: (response) => handleOnSuccess(payload, response) });
    } else {
      addMutation.mutate(payload, { onSuccess: (response) => handleOnSuccess(payload, response) });
    }
  };

  return (
    <CasReactHookForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      setFormMethods={setFormMethods}
      mode={mode}
    >
      {children}
    </CasReactHookForm>
  );
}
